import { toNumericValue } from '@staff/shared'
import type { Applicant, ApplicationDetailsApplicantResponse, UpdateApplicationPayload } from '@/services/application'
import {
  buildSharedUpdatePayload,
  hydrateApplicantsIntoForm,
  hydrateHouseholdIntoForm,
  optionalString,
} from './applicationFormMapper'
import { createInitialFormState, type CreateApplicationFormState } from './composables/applicationFormData'
import type { PropertyType } from './applicationFormConfig'
import type { DistributorCommitmentDetails } from '@/services/commitment'

// UpdateCommitmentApplicationSchema is .strict(): amountAppliedFor and isHighPrio are
// rejected outright (the backend derives the amount from propertyEstimation.value minus
// statedCashContribution), and property details are nested under propertyEstimation
// rather than sent flat. Hence the shared payload plus these two, and nothing else.
export const buildCommitmentUpdatePayload = (form: CreateApplicationFormState): UpdateApplicationPayload => {
  const municipality = optionalString(form.kommunEstimation)
  const propertyEstimation = {
    ...(toNumericValue(form.propertyEstimation) !== undefined ? { value: form.propertyEstimation } : {}),
    ...(form.propertyTypeEstimation ? { type: form.propertyTypeEstimation } : {}),
    ...(toNumericValue(form.sizeEstimation) !== undefined ? { size: form.sizeEstimation } : {}),
    ...(toNumericValue(form.monthlyFeeEstimation) !== undefined ? { monthlyFee: form.monthlyFeeEstimation } : {}),
    ...(municipality ? { municipality } : {}),
  }

  return {
    ...buildSharedUpdatePayload(form),
    ...(Object.keys(propertyEstimation).length > 0 ? { propertyEstimation } : {}),
    ...(toNumericValue(form.cashContribution) !== undefined ? { statedCashContribution: form.cashContribution } : {}),
  }
}

const toFormApplicant = (applicant: ApplicationDetailsApplicantResponse): Applicant => ({
  id: applicant.userId,
  userId: applicant.userId,
  personalNumber: Number(applicant.personalNumber) || 0,
  maritalStatus: applicant.maritalStatus ?? undefined,
  monthlyIncome: applicant.monthlyIncome ?? undefined,
  employmentInfo: applicant.employmentInfo,
  roles: applicant.roles,
  email: applicant.contactDetails.email ?? undefined,
  phoneNumber: applicant.contactDetails.phoneNumber ?? undefined,
})

/** Fills the shared form from a commitment's own read model. */
export const hydrateCommitmentForm = (form: CreateApplicationFormState, commitment: DistributorCommitmentDetails) => {
  Object.assign(form, createInitialFormState())

  form.type = 'commitmentApplication'
  form.brokerPersonnelUserId = commitment.brokerPersonnelUserId ?? ''
  form.cashContribution = commitment.statedCashContribution ?? undefined
  form.propertyEstimation = commitment.propertyEstimation?.value ?? undefined
  form.propertyTypeEstimation = (commitment.propertyEstimation?.type as PropertyType | null) ?? undefined
  form.sizeEstimation = commitment.propertyEstimation?.size ?? undefined
  form.monthlyFeeEstimation = commitment.propertyEstimation?.monthlyFee ?? undefined
  form.kommunEstimation = commitment.propertyEstimation?.municipality ?? ''
  form.comment = commitment.comment ?? ''

  hydrateApplicantsIntoForm(form, commitment.applicants.map(toFormApplicant))
  hydrateHouseholdIntoForm(form, commitment.household)
}
